/* eslint-disable react-refresh/only-export-components */
import {
  useState,
  type JSX,
  useCallback,
  type ComponentProps,
  type ReactEventHandler,
} from "react";

import { cx, sva } from "@mobsuccess-devops/styled-system/css";
import { styled } from "@mobsuccess-devops/styled-system/jsx";

import { composition } from "../../features/utils/composition";
import { createSvaContext } from "../../features/utils/sva-context";

import { imageAnatomy } from "./anatomy";

type ImageFallbackProps = ComponentProps<"div">;

function ImageFallback({ children, ...rest }: ImageFallbackProps): JSX.Element {
  const { fallback } = imageAnatomy.build();
  const { classes, meta } = useStyles();

  return (
    <div
      className={cx(classes.fallback, rest.className)}
      hidden={meta?.status === "error" ? undefined : true}
      {...fallback.attrs}
      {...rest}
    >
      {children}
    </div>
  );
}

export const StyledImageFallback = styled(ImageFallback);

type ImageLoaderProps = ComponentProps<"div">;

function ImageLoader({
  children,
  className,
  ...rest
}: ImageLoaderProps): JSX.Element {
  const { loader } = imageAnatomy.build();
  const { classes, meta } = useStyles();
  return (
    <div
      {...loader.attrs}
      className={cx(classes.loader, className)}
      hidden={meta?.status === "loading" ? undefined : true}
      {...rest}
    >
      {children}
    </div>
  );
}

export const StyledImageLoader = styled(ImageLoader);

type ImageImgProps = ComponentProps<"img">;

function ImageImg({ className, src, ...rest }: ImageImgProps): JSX.Element {
  const { image } = imageAnatomy.build();
  const { classes, meta } = useStyles();

  return (
    <img
      className={cx(classes.image, className)}
      hidden={meta?.status === "error" ? true : undefined}
      src={src}
      {...image.attrs}
      {...rest}
    />
  );
}

export const StyledImageImg = styled(ImageImg);

type ImageRootProps = ComponentProps<"div">;

export function ImageRoot({ children, ...props }: ImageRootProps): JSX.Element {
  const [status, setStatus] = useState<"loading" | "loaded" | "error">(
    "loading",
  );
  const { img, loader, fallback } = composition.split(children, {
    img: composition.single(StyledImageImg).required(),
    loader: composition.single(StyledImageLoader).required(),
    fallback: composition.single(StyledImageFallback).required(),
  });

  const variants = { status } as const;
  const classes = styles(variants);
  const { root } = imageAnatomy.build();

  const handleLoad = useCallback<ReactEventHandler<HTMLImageElement>>((e) => {
    switch (e.type) {
      case "load":
        setStatus("loaded");
        break;
      case "abort":
      case "error":
      case "invalid":
        setStatus("error");
        break;
    }
  }, []);

  return (
    <StylesProvider classes={classes} meta={variants}>
      <div
        {...root.attrs}
        {...props}
        onLoad={handleLoad}
        onError={handleLoad}
        onAbort={handleLoad}
        onInvalid={handleLoad}
        className={classes.root}
      >
        {loader}
        {img}
        {fallback}
      </div>
    </StylesProvider>
  );
}

const { styles, useStyles, StylesProvider } = createSvaContext(
  sva({
    slots: imageAnatomy.keys(),
    base: {
      root: {
        display: "contents",
      },
      fallback: {
        display: "contents",
      },
      image: {
        "&:not([src])": {
          display: "none",
        },
      },
    },
    variants: {
      status: {
        loading: {
          fallback: {
            display: "none",
          },
        },
        loaded: {
          loader: {
            display: "none",
          },
          fallback: {
            display: "none",
          },
        },
        error: {
          loader: {
            display: "none",
          },
          image: {
            display: "none",
          },
        },
      },
    },
  }),
);
